/**
 * Steel Welds Tool (BIM: Steel > Fabricate Elements > Welds)
 *
 * Two-element workflow: pick the primary member, then the secondary member.
 * The weld run is generated along the secondary member where it meets the
 * primary (approximated at the nearest approach between their axes). Shop vs
 * site weld, leg size and symbol come from OptionsBar tool properties.
 */

import { BaseTool } from '../BaseTool';
import { ThreeEvent } from '@react-three/fiber';
import * as THREE from 'three';
import { SteelFabricationManager, isSteelHostElement, resolveMemberFrame } from '../../core/SteelFabricationManager';
import i18n from '../../i18n';

export class SteelWeldTool extends BaseTool {
    id = 'steel_welds';
    name = 'Steel Welds';
    cursor = 'crosshair';

    private primaryId: string | null = null;

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        const hit = this.findElementHitAtPoint(event, el => isSteelHostElement(el));
        if (!hit) {
            this.notify(i18n.t('tools.notify.pickAStructuralSteelMember2'), 'warning');
            return;
        }

        if (!this.primaryId) {
            this.primaryId = hit.element.id;
            this.context.appStore.select([hit.element.id]);
            this.notify(i18n.t('tools.notify.primaryElementPickedNowPickThe'), 'info');
            return;
        }

        if (hit.element.id === this.primaryId) {
            this.notify(i18n.t('tools.notify.pickADifferentElementForThe'), 'warning');
            return;
        }

        this.createWeld(this.primaryId, hit.element.id);
        this.primaryId = null;
        this.context.appStore.select([]);
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        const hit = this.findElementHitAtPoint(event, el => isSteelHostElement(el));
        if (hit) {
            this.updatePreview(hit.point);
        } else {
            this.clearPreview();
        }
    }

    onPointerUp(_event: ThreeEvent<PointerEvent>): void { }

    onKeyDown(event: KeyboardEvent): void {
        // Undo the primary pick before the tool exits.
        if (event.key === 'Backspace' && this.primaryId) {
            this.primaryId = null;
            this.context?.appStore.select([]);
            event.preventDefault();
            return;
        }
        super.onKeyDown(event);
    }

    private createWeld(primaryId: string, secondaryId: string): void {
        if (!this.context) return;
        const bimStore = this.context.bimStore;
        const primary = bimStore.project.elements.get(primaryId);
        const secondary = bimStore.project.elements.get(secondaryId);
        const frameP = primary ? resolveMemberFrame(primary) : null;
        const frameS = secondary ? resolveMemberFrame(secondary) : null;
        if (!frameP || !frameS) {
            this.notify(i18n.t('tools.notify.bothMembersNeedUsableGeometry'), 'warning');
            return;
        }

        // Closest approach between the two member axes; the weld run is the
        // trace of the secondary's section around that junction point.
        const closest = closestPointsOnSegments(frameP.start, frameP.end, frameS.start, frameS.end);
        if (!closest) {
            this.notify(i18n.t('tools.notify.membersDoNotIntersectPlaceThem'), 'warning');
            return;
        }
        const { point } = closest;

        const props = bimStore.toolProperties;
        const sizeM = Math.max((props.steelWeldSize ?? 6) / 1000, 0.002);
        const dirP = frameP.end.clone().sub(frameP.start).normalize();
        const dirS = frameS.end.clone().sub(frameS.start).normalize();
        const side = new THREE.Vector3().crossVectors(dirP, dirS);
        if (side.lengthSq() < 1e-8) side.set(0, 0, 1);
        side.normalize();

        // Short run along the secondary axis on both sides of the junction.
        const runLen = Math.min(frameS.depth * 1.5, frameS.start.distanceTo(frameS.end) / 2);
        const p1 = point.clone().addScaledVector(dirS, -runLen / 2).addScaledVector(side, sizeM);
        const p2 = point.clone().addScaledVector(dirS, runLen / 2).addScaledVector(side, sizeM);

        const weld = SteelFabricationManager.createWeld(
            [p1, p2],
            primaryId,
            secondaryId,
            {
                location: props.steelWeldType === 'Site' ? 'Site' : 'Shop',
                sizeM,
                symbol: 'Fillet',
            }
        );
        if (!weld) return;

        bimStore.addElement(weld);
        this.notify(i18n.t('tools.notify.weldPlaced', { type: weld.type, size: (sizeM * 1000).toFixed(0) }), 'success');
        this.clearPreview();
    }

    private updatePreview(point: THREE.Vector3): void {
        this.context?.appStore.setToolPreview({
            type: 'steel_weld',
            position: point,
        });
    }
}

/** Closest points between two finite segments in world space. */
function closestPointsOnSegments(
    a0: THREE.Vector3, a1: THREE.Vector3,
    b0: THREE.Vector3, b1: THREE.Vector3
): { point: THREE.Vector3; distance: number } | null {
    const u = a1.clone().sub(a0);
    const v = b1.clone().sub(b0);
    const w0 = a0.clone().sub(b0);
    const a = u.dot(u);
    const b = u.dot(v);
    const c = v.dot(v);
    const d = u.dot(w0);
    const e = v.dot(w0);
    const denom = a * c - b * b;
    if (denom < 1e-9) return null;

    let sc = (b * e - c * d) / denom;
    let tc = (a * e - b * d) / denom;
    sc = THREE.MathUtils.clamp(sc, 0, 1);
    tc = THREE.MathUtils.clamp(tc, 0, 1);

    const pa = a0.clone().addScaledVector(u, sc);
    const pb = b0.clone().addScaledVector(v, tc);
    const mid = pa.clone().add(pb).multiplyScalar(0.5);
    return { point: mid, distance: pa.distanceTo(pb) };
}
